import { pb, errorText } from './pb';
import type {
  Campaign,
  Decision,
  Influencer,
  Location,
  Product,
  Run,
  SceneReview,
} from '../../../shared/types';

/**
 * Calls to the server routes that the browser cannot do directly.
 *
 * `runs` and `jobs` are superuser-only collections, so starting, advancing or
 * cancelling a pipeline all go through validated endpoints. A user cannot forge
 * a job or aim one at someone else's campaign.
 */

async function post<T>(path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: pb.authStore.token,
    },
    body: JSON.stringify(body ?? {}),
  });

  const text = await res.text();
  let data: unknown = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    /* non-JSON error body */
  }

  if (!res.ok) {
    const message =
      (data as { message?: string } | null)?.message || text || `Request failed (${res.status})`;
    throw new Error(message);
  }
  return data as T;
}

export type StartKind =
  | 'generate_script'
  | 'render_images'
  | 'render_videos'
  | 'assemble'
  // Free: return to a review stage using the already-generated media. No job
  // is enqueued and nothing is billed.
  | 'resume_image_review'
  | 'resume_video_review';

export function startCampaign(campaignId: string, kind: StartKind) {
  return post<{ run: string; kind: string }>(`/api/campaigns/${campaignId}/start`, { kind });
}

/** How many review rows exist per kind — drives the "go back to…" options. */
export async function reviewCounts(
  campaignId: string
): Promise<{ images: number; videos: number }> {
  const [images, videos] = await Promise.all([
    pb.collection('scene_reviews').getList(1, 1, {
      filter: `campaign = "${campaignId}" && kind = "image"`,
    }),
    pb.collection('scene_reviews').getList(1, 1, {
      filter: `campaign = "${campaignId}" && kind = "video"`,
    }),
  ]);
  return { images: images.totalItems, videos: videos.totalItems };
}

/**
 * Submits a completed review round. The server re-checks that every scene has a
 * decision before it enqueues anything — the button being enabled is not the
 * authority on that.
 */
export function submitReview(campaignId: string, kind: 'image' | 'video') {
  return post<{ ok: boolean; run: string }>(`/api/campaigns/${campaignId}/submit-review`, { kind });
}

/**
 * Deletes one scene entirely during image/video review — the scene JSON and its
 * review rows (both kinds) are removed server-side in one transaction, so the
 * scene's speech can never leak into the caption-correction prompt and no
 * orphaned review row can block submission. Remaining scenes keep their
 * original numbers (gaps are fine everywhere downstream).
 */
export function deleteScene(campaignId: string, sceneIndex: number) {
  return post<{ ok: boolean; remainingScenes: number }>(
    `/api/campaigns/${campaignId}/delete-scene`,
    { sceneIndex }
  );
}

/**
 * "One scene in the finished video is wrong." Clones a done campaign into a
 * new one (a revision) that starts at video review with every clip carried
 * over and pre-approved, so only the wrong scene(s) need a re-render
 * decision. The original campaign and its video are left untouched. Nothing
 * is generated or billed until the revision's decisions are submitted.
 */
export function reviseCampaign(campaignId: string) {
  return post<{ campaign: string; seq: number; run: string }>(
    `/api/campaigns/${campaignId}/revise`
  );
}

/**
 * Wipes a demo campaign back to a blank draft (runs, log, reviews, final
 * links) so the presentation can be replayed. Admin-only; refused on a real
 * campaign — see the route for why.
 */
export function resetDemo(campaignId: string) {
  return post<{ ok: boolean; removed: Record<string, number> }>(
    `/api/campaigns/${campaignId}/demo-reset`
  );
}

export function cancelRun(runId: string) {
  return post<{ ok: boolean; dropped: number }>(`/api/runs/${runId}/cancel`);
}

export function runLogUrl(runId: string): string {
  return `/api/runs/${runId}/log.txt`;
}

/** Downloads a run log, sending the auth token that a plain link cannot. */
export async function downloadRunLog(runId: string, filename: string): Promise<void> {
  const res = await fetch(runLogUrl(runId), {
    headers: { Authorization: pb.authStore.token },
  });
  if (!res.ok) throw new Error(`Could not fetch the log (${res.status})`);

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ─── collections ──────────────────────────────────────────────────────────────

export async function listCampaigns(): Promise<Campaign[]> {
  return pb.collection('campaigns').getFullList({
    sort: '-created',
    expand: 'influencer,product,created_by',
    batch: 200,
  });
}

export async function getCampaign(id: string): Promise<Campaign> {
  return pb.collection('campaigns').getOne(id, {
    expand: 'influencer,product,created_by',
  });
}

export async function listInfluencers(): Promise<Influencer[]> {
  return pb.collection('influencers').getFullList({ sort: 'name', batch: 200 });
}

export async function listProducts(): Promise<Product[]> {
  return pb.collection('products').getFullList({ sort: 'name', batch: 200 });
}

export async function listLocations(): Promise<Location[]> {
  return pb.collection('locations').getFullList({ sort: 'sort', batch: 500 });
}

export async function listReviews(
  campaignId: string,
  kind: 'image' | 'video'
): Promise<SceneReview[]> {
  return pb.collection('scene_reviews').getFullList({
    filter: `campaign = "${campaignId}" && kind = "${kind}"`,
    sort: 'scene_index',
    batch: 200,
  });
}

export async function listRuns(campaignId?: string): Promise<Run[]> {
  return pb.collection('runs').getFullList({
    ...(campaignId ? { filter: `campaign = "${campaignId}"` } : {}),
    sort: '-created',
    expand: 'campaign,started_by',
    batch: 100,
  });
}

export async function activeRun(campaignId: string): Promise<Run | null> {
  const list = await pb.collection('runs').getList(1, 1, {
    filter: `campaign = "${campaignId}" && (status = "running" || status = "cancelling")`,
    sort: '-run_number',
  });
  // Cast at the boundary: getList can't infer the record type from context the
  // way the getFullList calls above can, so it comes back as RecordModel.
  return (list.items[0] as unknown as Run) ?? null;
}

// ─── writes ───────────────────────────────────────────────────────────────────

export interface ReviewDecision {
  id: string;
  decision: Decision;
  note?: string;
  promptEdit?: string;
  holdingProduct?: '' | 'YES' | 'NO';
  inPoint?: number;
  outPoint?: number;
}

/**
 * Saves one scene's verdict.
 *
 * v1 needed a strict write order here — the note and prompt edit had to land in
 * columns G:I *before* the decision in column F, because n8n polled for a
 * non-blank decision cell and would otherwise read a half-written verdict.
 * One record, one write, no protocol.
 */
export async function saveDecision(d: ReviewDecision): Promise<SceneReview> {
  return pb.collection('scene_reviews').update(d.id, {
    decision: d.decision,
    note: d.note ?? '',
    prompt_edit: d.promptEdit ?? '',
    ...(d.holdingProduct !== undefined ? { holding_product: d.holdingProduct } : {}),
    ...(d.inPoint !== undefined ? { in_point: d.inPoint } : {}),
    ...(d.outPoint !== undefined ? { out_point: d.outPoint } : {}),
  });
}

/**
 * Swaps a review row's image for one from the library and approves it — the
 * "this existing image works, don't re-render" path. The video stage animates
 * whatever URL sits on the approved row; it cannot tell the difference.
 */
export async function replaceReviewImage(id: string, url: string): Promise<SceneReview> {
  return pb.collection('scene_reviews').update(id, {
    media_url: url,
    drive_url: url,
    decision: 'approve',
    failed: false,
    fail_reason: '',
  });
}

export interface CreateCampaignInput {
  title: string;
  productId: string;
  influencerId: string;
  mode: 'ai' | 'custom';
  angle: string;
  customScript: string;
  aspectRatio: '9:16' | '16:9';
  customLocations: string[];
  /** Admin only — the server clears it for anyone else. */
  isDemo?: boolean;
}

export async function createCampaign(input: CreateCampaignInput): Promise<Campaign> {
  return pb.collection('campaigns').create({
    title: input.title,
    product: input.productId,
    influencer: input.influencerId,
    created_by: pb.authStore.record?.id,
    mode: input.mode,
    angle: input.angle,
    custom_script: input.customScript,
    aspect_ratio: input.aspectRatio,
    custom_locations: input.customLocations,
    scenes: [],
    stage: 'draft',
    stage_message: '',
    is_demo: Boolean(input.isDemo),
  });
}

/**
 * Updates a campaign, refusing to overwrite someone else's concurrent edit.
 *
 * `expectedUpdated` is the `updated` value the form was loaded with. If the
 * record has moved on, the caller is told to reload rather than silently
 * clobbering — v1 read the whole row, mutated it in memory and wrote it back,
 * racing every other writer.
 */
export async function updateCampaign(
  id: string,
  data: Partial<Campaign>,
  expectedUpdated?: string
): Promise<Campaign> {
  if (expectedUpdated) {
    const current = await pb.collection('campaigns').getOne(id);
    if (current.updated !== expectedUpdated) {
      throw new Error(
        'Someone else changed this campaign while you were editing. Reload to see their changes.'
      );
    }
  }
  return pb.collection('campaigns').update(id, data);
}

export async function submitRequest(subject: string, message: string, imageUrl = '') {
  return pb.collection('requests').create({
    user: pb.authStore.record?.id,
    subject,
    message,
    image_url: imageUrl,
    done: false,
  });
}

export { errorText };
